import { threadLastVisitedAtAtom } from "@/atoms/threadsAtoms.ts";
import { threadAwaitsUser } from "@/components/threads/shared.tsx";
import { useSocket } from "@/contexts/SocketContext.tsx";
import { badgeQueries } from "@/hooks/useBadge.ts";
import { routineQueries } from "@/hooks/useRoutines.ts";
import { getThreadFilterParams, type ThreadFilterMode } from "@/shared/threadFilters.ts";
import {
  asThreadListPage,
  mapThreadListPages,
  regroupThread,
  sameThreadSelection,
  type ThreadListPage,
  threadListRows,
} from "@/shared/threadListPages.ts";
import { useThrottledInvalidation } from "@/shared/useThrottledInvalidation.ts";
import { api } from "@/lib/http.ts";
import type { ServerToClientThreadEvents } from "@/types/socketIo.ts";
import type {
  CreateNewTopicThreadReq,
  CreateNewTopicThreadResp,
  GetThreadResp,
  GetThreadsGroupedResp,
  GetThreadsQuery,
  UpdateThreadReq,
  UpdateThreadVisitedAtResp,
} from "@/types/thread.ts";
import {
  affectsThreadCard,
  affectsThreadListPlacement,
  defaultChannels,
  defaultStatusFilter,
} from "@/types/thread.ts";
import * as Sentry from "@sentry/react";
import { useGateValue } from "@statsig/react-bindings";
import {
  infiniteQueryOptions,
  QueryClient,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "wouter";
export const THREAD_PAGE_SIZE = 5;

/** Input type for threads list query - derived from schema, page handled by infinite query */
type ThreadsListParams = Partial<Omit<GetThreadsQuery, "page">>;

/**
 * Every thread query key and its options, ordered most generic to most
 * specific. The `all*` entries are prefixes to invalidate against; `list` and
 * `detail` each address one query. (The React Query convention spells the
 * prefixes `lists`/`details`, which reads too close to the singulars.)
 *
 * Invalidate `allLists()` rather than `all()` unless you really mean everything
 * under threads — `all()` also matches mounted detail queries, so using it for
 * an ordering change refetches whatever thread the user is reading (BIG-6465).
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys
 */
export const threadQueries = {
  all: () => ["threads"] as const,
  allDetails: () => [...threadQueries.all(), "detail"] as const,
  detail: (id: number) =>
    queryOptions({
      queryKey: [...threadQueries.allDetails(), id] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<GetThreadResp>(`/api/a/threads/${id}`, {
          signal,
        });
        return response.data;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
  allLists: () => [...threadQueries.all(), "list"] as const,
  list: ({
    status,
    activityState,
    labels,
    order,
    limit = THREAD_PAGE_SIZE,
    channel = defaultChannels,
    needsAttentionFirst = false,
  }: ThreadsListParams = {}) => {
    const keyParams = { status, activityState, labels, order, limit, channel, needsAttentionFirst };
    return infiniteQueryOptions({
      queryKey: [...threadQueries.allLists(), keyParams] as const,
      queryFn: async ({ pageParam = 0, queryKey, signal }) => {
        const [, , options] = queryKey;
        const queryParams = new URLSearchParams();

        if (options.limit) {
          queryParams.set("limit", options.limit.toString());
        }
        if (options.status && options.status.length > 0) {
          queryParams.set("status", options.status.join(","));
        }
        if (options.activityState && options.activityState.length > 0) {
          queryParams.set("activityState", options.activityState.join(","));
        }
        if (options.labels && options.labels.length > 0) {
          queryParams.set("labels", options.labels.join(","));
        }
        if (options.order) {
          queryParams.set("order", options.order);
        }
        if (options.channel && options.channel.length > 0) {
          queryParams.set("channel", options.channel.join(","));
        }
        // Sent only when on: the server reads an absent param as date order,
        // so omitting it and sending false are the same request.
        if (options.needsAttentionFirst) {
          queryParams.set("needsAttentionFirst", "true");
        }
        queryParams.set("page", pageParam.toString());
        // Pins and the remainder as two arrays. Page 0 carries the pins; the
        // remainder is cut by `limit` alone, so paging reads its length.
        queryParams.set("groupBy", "pinned");

        const response = await api.get<GetThreadsGroupedResp | GetThreadResp[]>(
          `/api/a/threads?${queryParams.toString()}`,
          {
            signal,
          },
        );

        return asThreadListPage(response.data);
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        if (lastPage.rest.length < limit) {
          return undefined;
        }
        return lastPageParam + 1;
      },
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true, // Refetch when window is focused
      staleTime: 5000, // Shorter stale time to ensure fresh data on focus
      refetchOnMount: true,
      // Hold the rows while the key moves, but only when the move is this same
      // list in a different order: the menu flipping `needsAttentionFirst`, or
      // a cold load resolving it behind the first fetch. Both land on a key
      // with no cached pages, and the pane would otherwise drop to its loader
      // and back to nearly the same rows.
      //
      // Scoped rather than unconditional, because the filter is in this key
      // too. Holding across a filter change would leave the previous view's
      // threads under the new heading, still clickable, with nothing saying so.
      placeholderData: (previous, previousQuery) =>
        sameThreadSelection(previousQuery?.queryKey, keyParams) ? previous : undefined,
      select: (data) => data.pages,
    });
  },
};

export const useThreads = ({
  status,
  activityState,
  labels,
  order,
  limit = THREAD_PAGE_SIZE,
  channel,
  needsAttentionFirst,
}: ThreadsListParams = {}) => {
  const queryClient = useQueryClient();
  const queryOptions = useMemo(
    () =>
      threadQueries.list({
        status,
        activityState,
        labels,
        order,
        limit,
        channel,
        needsAttentionFirst,
      }),
    [status, activityState, labels, order, limit, channel, needsAttentionFirst],
  );

  const {
    data: threadPages,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery(queryOptions);

  // Error handling
  useEffect(() => {
    if (error) {
      console.error("Failed to fetch threads:", error);
      Sentry.captureException(error, {
        tags: {
          component: "useThreads",
          operation: "fetchThreads",
        },
        extra: {
          status,
          order,
          limit,
          threadPagesCount: threadPages?.length || 0,
        },
      });
      // TODO: Add user notification toast here
    }
  }, [error, status, order, limit, threadPages]);

  const mutate = useCallback(async () => {
    // Every list variant, not just this hook's — a caller refreshing "the
    // threads" means the filter tabs too. Detail queries are deliberately out
    // of scope.
    await queryClient.invalidateQueries({
      queryKey: threadQueries.allLists(),
      exact: false,
    });
  }, [queryClient]);

  const loadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return {
    error,
    isLoading,
    isFetchingNextPage,
    mutate,
    threadPages: threadPages || [],
    isValidating: isFetching,
    hasNextPage,
    loadMore,
  };
};

/**
 * Update the lastVisitedAt field for a thread on mount and on explicit refresh. We should tightly couple
 * these calls to updates to the visible thread items.
 *
 * On a thread message fetch (feed or chat) load, whether that's from an explicit reload or the first
 * page load that session, we store the thread.lastVisitedAt value in the atom. Then we update the
 * thread.lastVisitedAt value on the server but _do not update the atom_. Therefore, the value in the
 * atom is intentionally "off by 1" from the server value.
 *
 * We use the atom value to render "caught up" markers in the UI. So if we just updated the atom to the
 * server value immediately, then the UI would appear to be fully caught every time the user viewed the thread.
 *
 * It's very important to tightly couple updates to the atom/server value to actual changes to the thread items.
 * Otherwise, if there are new messages on the server that the client doesn't have, we would show those below
 * the "caught up" marker.
 */
export const useThreadVisitedAt = ({
  thread,
  mutateThread,
}: {
  thread: GetThreadResp;
  mutateThread: () => Promise<void>;
}) => {
  const queryClient = useQueryClient();
  const [lastVisitedAtMap, setLastVisitedAtAtom] = useAtom(threadLastVisitedAtAtom);

  const visitedAtMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<UpdateThreadVisitedAtResp>(
        `/api/a/threads/${thread.id}/visited`,
        {},
      );
      return response.data;
    },
    onError: (error: Error) => {
      console.error("Failed to update thread visited status:", error);
      Sentry.captureException(error, {
        tags: {
          component: "useThreadVisitedAt",
          operation: "updateVisitedAt",
        },
        extra: {
          threadId: thread.id,
          threadStatus: thread.status,
        },
      });
      // TODO: Add user notification toast here
    },
  });

  const updateLastVisitedAt = useCallback(async () => {
    setLastVisitedAtAtom((prev) => {
      const newMap = new Map(prev);
      newMap.set(thread.id, thread.lastVisitedAt ? new Date(thread.lastVisitedAt) : null);
      return newMap;
    });

    const threadUpdate = await visitedAtMutation.mutateAsync();
    updateThreadOptimistically(queryClient, thread.id, threadUpdate);
    await mutateThread();
  }, [
    visitedAtMutation,
    setLastVisitedAtAtom,
    thread.id,
    thread.lastVisitedAt,
    mutateThread,
    queryClient,
  ]);

  const isMounted = useRef(false);

  // On mount, store the lastVisitedAt in the atom if it has not been set yet
  useEffect(() => {
    // only try to run once per lifetime, so we don't set visited multiple times during the first time
    // this hook is mounted
    if (isMounted.current) return;
    isMounted.current = true;

    // check the atom - if another hook instance has already set the value, we don't need to do it again
    // not just a falsy check, on the first session a user will not have a lastVisitedAt, we need to respect that null
    if (lastVisitedAtMap.has(thread.id)) return;
    void updateLastVisitedAt();
  }, [lastVisitedAtMap, updateLastVisitedAt, thread.id]);

  return [lastVisitedAtMap.get(thread.id), updateLastVisitedAt, visitedAtMutation] as const;
};

/** Update the thread in the query cache (both detail and list queries). */
export const updateThreadOptimistically = (
  queryClient: QueryClient,
  threadId: number,
  diff: Partial<GetThreadResp>,
) => {
  // Update the thread detail query
  queryClient.setQueryData(
    threadQueries.detail(threadId).queryKey,
    (oldData: GetThreadResp | undefined) => {
      if (!oldData) return oldData;
      return { ...oldData, ...diff };
    },
  );

  // Update the thread in all list queries (infinite query structure)
  queryClient.setQueriesData<InfiniteThreadList>(
    { queryKey: threadQueries.allLists(), exact: false },
    (oldData) => {
      if (!oldData?.pages) return oldData; // Skip non-infinite queries
      return {
        ...oldData,
        pages: mapThreadListPages(oldData.pages, (thread) =>
          thread.id === threadId ? { ...thread, ...diff } : thread,
        ),
      };
    },
  );
};

/** A cached thread list: one `ThreadListPage` per fetched page. */
type InfiniteThreadList = { pages: ThreadListPage[]; pageParams: number[] };

/**
 * Move a thread between the pinned group and the remainder of every cached
 * list, to match the `pinnedAt` its row now carries. Called after a pin diff
 * has been merged into the row, so the list regroups on the socket event
 * rather than on the refetch that follows it. Each list seats the row under its
 * own `order` and, where it is ordered needs-you first, behind the rows
 * awaiting the user — both read off that list's query key.
 */
export const regroupThreadInLists = (queryClient: QueryClient, threadId: number) => {
  for (const query of queryClient.getQueryCache().findAll({ queryKey: threadQueries.allLists() })) {
    const [, , params] = query.queryKey as ReturnType<typeof threadQueries.list>["queryKey"];
    queryClient.setQueryData<InfiniteThreadList>(query.queryKey, (oldData) => {
      if (!oldData?.pages) return oldData;
      const pages = regroupThread(
        oldData.pages,
        threadId,
        params?.order ?? "desc",
        params?.needsAttentionFirst ? threadAwaitsUser : undefined,
      );
      return pages === oldData.pages ? oldData : { ...oldData, pages };
    });
  }
};

/**
 * Update thread to "in progress" state by setting both activityState and artifact status.
 * This ensures the UI immediately shows the thread as in-progress.
 *
 * Also clears the active artifact's `actions` array and resets the server-
 * emitted `card.status` so the status badge + action buttons flip to the
 * in-progress state the instant the user clicks, before the POST resolves.
 *
 * Callers can pass a `cardBody` override (derived from the fired action's
 * `pendingBody`, e.g. "Sending email to Frank, Kiran and 2 others.") to
 * replace the card copy until the next refetch / socket update lands.
 */
export const updateThreadToInProgress = (
  queryClient: QueryClient,
  threadId: number,
  cardBody?: string,
) => {
  const updateFn = (thread: GetThreadResp | undefined): GetThreadResp | undefined => {
    if (!thread) return thread;

    const updatedThread: GetThreadResp = {
      ...thread,
      activityState: "running" as const,
      card: thread.card
        ? {
            ...thread.card,
            status: "inProgress" as const,
            body: cardBody ?? thread.card.body,
          }
        : thread.card,
    };

    if (thread.activeMessage?.artifacts?.[0] && "status" in thread.activeMessage.artifacts[0]) {
      const artifact = thread.activeMessage.artifacts[0];
      // Drop the declarative `actions` so the split-pill disappears instantly —
      // it'll come back from the server if the thread re-enters
      // `waitingForUserAction` later. Cast via `unknown` because the narrow
      // literal types on each artifact subtype don't surface `actions` on
      // every branch even though the schema marks it optional everywhere.
      updatedThread.activeMessage = {
        ...thread.activeMessage,
        artifacts: [
          {
            ...artifact,
            status: "inProgress",
            actions: undefined,
          } as unknown as typeof artifact,
        ],
      };
    }

    return updatedThread;
  };

  // Update the thread detail query
  queryClient.setQueryData(threadQueries.detail(threadId).queryKey, updateFn);

  // Update the thread in all list queries (infinite query structure)
  queryClient.setQueriesData<InfiniteThreadList>(
    { queryKey: threadQueries.allLists(), exact: false },
    (oldData) => {
      if (!oldData?.pages) return oldData; // Skip non-infinite queries
      return {
        ...oldData,
        pages: mapThreadListPages(oldData.pages, (thread) =>
          thread.id === threadId ? (updateFn(thread) ?? thread) : thread,
        ),
      };
    },
  );
};

export const topicThreadURL = (threadId: number) => `/api/a/threads/${threadId}`;

/**
 * Hook to update a thread's visitedAt timestamp.
 * This is used to track when a user last viewed the thread.
 */
export const useUpdateThreadVisitedAt = ({ threadId }: { threadId: number | undefined }) => {
  const updateVisitedAt = useCallback(async () => {
    // No thread loaded yet — nothing to mark visited. Guarding here (rather than
    // with a sentinel id at the call site) means we never POST to a bogus id.
    if (threadId === undefined) return;

    const response = await api.post(`/api/a/threads/${threadId}/visited`, {});

    return response.data;
  }, [threadId]);

  return updateVisitedAt;
};

/**
 * Coalescing window for thread-list invalidation. Shorter than the shared 2s
 * default because this window visibly delays new and reordered threads
 * appearing in the list.
 */
export const THREAD_LIST_INVALIDATE_THROTTLE_MS = 1000;

/**
 * Emissions that refetch immediately before the throttle engages. Lower than
 * the shared default of 5, because thread events arrive per-thread and an
 * account with many concurrent runs spends an allowance far faster than a
 * calendar sync does.
 */
export const THREAD_LIST_INVALIDATE_BURST_ALLOWANCE = 3;

/**
 * Returns a trigger that invalidates the thread lists, coalescing bursts into
 * one refetch so an account running many concurrent agent threads can't turn
 * its event stream into a request storm. Leading-edge: an ordinary single event
 * still refetches immediately.
 */
const useThrottledThreadListInvalidation = () => {
  const queryClient = useQueryClient();

  const flush = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: threadQueries.allLists() });
  }, [queryClient]);

  return useThrottledInvalidation(flush, {
    throttleMs: THREAD_LIST_INVALIDATE_THROTTLE_MS,
    burstAllowance: THREAD_LIST_INVALIDATE_BURST_ALLOWANCE,
  });
};

/**
 * Global hook to listen for all thread:mutated socket events. Updates the corresponding thread query:
 * 1. Applies the event diff as an optimistic update
 * 2. Invalidates it
 *
 * **THIS SHOULD ONLY BE MOUNTED ONCE, AT THE TOP LEVEL OF THE APP**. Otherwise we'll just duplicate
 * the aforementioned side effects.
 *
 * Re: invalidation and potential inefficiency with doing this for all threads:
 * > Query Invalidation plays really nice together with WebSockets. This approach avoids
 * > the problem of over pushing, because if we receive an event for an entity that we are
 * > not interested in at the moment, nothing will happen. For example, if we are currently
 * > on the Profile page, and we receive an update for Posts, invalidateQueries will make
 * > sure that the next time we get to our Posts page, it will be refetched. However, it
 * > will not refetch it right away, because we have no active observers. If we never go to
 * > that page again, the pushed update would be completely unnecessary.
 * @see https://tkdodo.eu/blog/using-web-sockets-with-react-query
 */
export const useThreadMutatedSocket = () => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const { trigger: invalidateThreadLists, cancelPending } = useThrottledThreadListInvalidation();

  useEffect(() => {
    if (!socket) {
      return;
    }

    // Handle new thread creation - invalidate list queries to refetch
    const handleThreadCreated = (
      _eventData: Parameters<ServerToClientThreadEvents["thread:created"]>[0],
    ) => {
      // The payload is just an id, and which filter variants the new thread
      // belongs to is server-derived, so a refetch is the only option.
      invalidateThreadLists();
    };

    // Handle existing thread mutations - applies partial diff
    const handleThreadMutated = (
      eventData: Parameters<ServerToClientThreadEvents["thread:mutated"]>[0],
    ) => {
      const { threadId, diff } = eventData;

      // An empty diff has nothing to merge and nothing to refetch on, but would
      // still churn every cached list page rebuilding identical rows.
      if (!diff || Object.keys(diff).length === 0) return;

      // Cancel first: an in-flight GET issued before this event carries a
      // pre-mutation snapshot, and React Query writes its response over the
      // cache when it lands. Without this, a diff that needs no refetch below
      // gets silently reverted and nothing refreshes it for a full staleTime.
      void queryClient.cancelQueries({ queryKey: threadQueries.detail(threadId).queryKey });

      // Apply optimistic update to existing threads
      updateThreadOptimistically(queryClient, threadId, diff);

      // A pin is the one placement change the client can apply itself: the
      // groups are the list's own structure, so the row moves now and the
      // refetch below only reconciles its slot among the remainder.
      if ("pinnedAt" in diff) {
        regroupThreadInLists(queryClient, threadId);
      }

      // The patch above updates the row in place but leaves it at its old
      // position, and can't know which filter variants the thread now belongs
      // to — order and membership are both server-derived. So a placement
      // change still needs a refetch; without it the list waits for the 30s
      // poll to catch up.
      //
      // Key presence is enough: the server sends only the columns a write
      // actually touched (`buildThreadDiff`).
      if (affectsThreadListPlacement(diff)) {
        invalidateThreadLists();
      }

      // Just this thread's detail query, not `allDetails()` — the point of the
      // list/detail key split is that another thread's mutation must not
      // refetch whatever the user is reading. This one changed, and its `card`
      // is server-derived, so the merged patch above cannot repair it.
      if (affectsThreadCard(diff)) {
        void queryClient.invalidateQueries({
          queryKey: threadQueries.detail(threadId).queryKey,
        });
      }
    };

    // Handle deletion - evict rather than refetch. This fires on every tab of
    // the account, including the ones that didn't do the deleting and are still
    // holding a card (or an open panel) for a thread that no longer resolves.
    const handleThreadDeleted = (
      eventData: Parameters<ServerToClientThreadEvents["thread:deleted"]>[0],
    ) => {
      const { threadId } = eventData;
      void queryClient.cancelQueries({ queryKey: threadQueries.detail(threadId).queryKey });
      queryClient.removeQueries({ queryKey: threadQueries.detail(threadId).queryKey });
      invalidateThreadLists();
      // Unthrottled, unlike the lists: a routine's run history is a separate
      // query the throttle does not cover, and deletions are rare enough that
      // there is no storm to smooth.
      queryClient.invalidateQueries({ queryKey: routineQueries.all() });
    };

    socket.on("thread:created", handleThreadCreated);
    socket.on("thread:mutated", handleThreadMutated);
    socket.on("thread:deleted", handleThreadDeleted);

    return () => {
      socket.off("thread:created", handleThreadCreated);
      socket.off("thread:mutated", handleThreadMutated);
      socket.off("thread:deleted", handleThreadDeleted);
      // The socket nulls on disconnect without unmounting this hook, so the
      // throttle's own unmount cleanup never runs. Dropping the scheduled flush
      // keeps it from stamping the window mid-outage, which would push the first
      // post-reconnect refetch a window later.
      cancelPending();
    };
  }, [socket, queryClient, invalidateThreadLists, cancelPending]);
};

/**
 * Core hook for fetching a single thread.
 * Provides basic thread data and revalidation function.
 */
export const useTopicThread = ({ threadId }: { threadId: number }) => {
  const queryClient = useQueryClient();
  const { data: thread, isLoading, error } = useQuery(threadQueries.detail(threadId));

  // The thread does not resolve for this account, so there is nothing for a
  // view to show. `verifyThreadOwnership` answers 403 both for a thread owned
  // by another account — a `?thread=N` URL that outlived an account switch, a
  // shared or bookmarked link — and for one that no longer exists at all,
  // which is what a deleted thread looks like from here; 404 covers an id that
  // was never a thread. Surfaced as a clean flag so the view can leave rather
  // than sit on the address rendering a raw error.
  const errorStatus = isAxiosError(error) ? error.response?.status : undefined;
  const isThreadGone = errorStatus === 403 || errorStatus === 404;

  const revalidate = useCallback(async () => {
    void queryClient.invalidateQueries({
      queryKey: threadQueries.detail(threadId).queryKey,
    });
  }, [threadId, queryClient]);

  return {
    isLoading,
    error,
    isThreadGone,
    thread,
    revalidate,
  };
};

/**
 * Hook to expose thread archiving using React Query.
 */
export const useArchiveThread = ({ threadId }: { threadId: number }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<Pick<GetThreadResp, "status" | "id">>(
        `/api/a/threads/${threadId}/archive`,
        {},
      );
      return response.data;
    },
    onMutate: async () => {
      // Optimistically update the thread status
      updateThreadOptimistically(queryClient, threadId, {
        status: "completed",
      });
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: threadQueries.detail(threadId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: threadQueries.allLists() });
      // Archiving changes what the awareness badge counts (it drops out of the
      // needs-attention / unseen-completed tallies), so refresh the badge.
      queryClient.invalidateQueries({ queryKey: badgeQueries.all() });
    },
    onError: (error: Error) => {
      console.error("Failed to archive thread:", error);
      Sentry.captureException(error, {
        tags: {
          component: "useArchiveThread",
          operation: "archiveThread",
        },
        extra: {
          threadId,
        },
      });
      // Revert the optimistic update. Lists carry it too, so detail alone would
      // leave every row reading archived against a server that still has it.
      queryClient.invalidateQueries({
        queryKey: threadQueries.detail(threadId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: threadQueries.allLists() });
    },
  });
};

/**
 * Pin or unpin a thread (BIG-7196). One hook for both directions because the
 * two differ only in the path — the cache work is identical, and a pin that
 * couldn't be undone the same way would be a trap.
 *
 * No optimistic patch: `pinnedAt` decides where the thread sits, and the order
 * is server-derived. Patching the row in place would leave it visibly pinned but
 * still sitting in its old slot until the refetch landed.
 */
export const useSetThreadPinned = ({ threadId }: { threadId: number }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pinned: boolean) => {
      await api.post(`/api/a/threads/${threadId}/${pinned ? "pin" : "unpin"}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: threadQueries.detail(threadId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: threadQueries.allLists() });
    },
    onError: (error: Error) => {
      Sentry.captureException(error, {
        tags: { component: "useSetThreadPinned", operation: "setThreadPinned" },
        extra: { threadId },
      });
    },
  });
};

/**
 * Delete a thread and everything belonging to it (BIG-7196). Irreversible.
 *
 * Removes the detail query rather than invalidating it: invalidating would
 * refetch a thread that no longer exists and cache the 404 as an error state,
 * which the panel would render on its way out.
 */
export const useDeleteThread = ({ threadId }: { threadId: number }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.post(`/api/a/threads/${threadId}/delete`, {});
    },
    onSuccess: () => {
      // Cancel before removing, for the same reason the socket handler does: a
      // GET issued before the delete is still in flight, and React Query writes
      // its response into the cache when it lands — repopulating the detail
      // entry for a thread the server no longer has.
      void queryClient.cancelQueries({ queryKey: threadQueries.detail(threadId).queryKey });
      queryClient.removeQueries({ queryKey: threadQueries.detail(threadId).queryKey });
      queryClient.invalidateQueries({ queryKey: threadQueries.allLists() });
      // A routine's run history is its own query, keyed off the routine rather
      // than the thread, so it is not a thread list and keeps listing a run the
      // server now hides.
      queryClient.invalidateQueries({ queryKey: routineQueries.all() });
      // The thread's unread and needs-attention rows are gone with it, so the
      // awareness badge is now counting things that don't exist.
      queryClient.invalidateQueries({ queryKey: badgeQueries.all() });
    },
    onError: (error: Error) => {
      Sentry.captureException(error, {
        tags: { component: "useDeleteThread", operation: "deleteThread" },
        extra: { threadId },
      });
    },
  });
};

/**
 * Response type for creating a thread snapshot
 */
interface CreateSnapshotResp {
  id: number;
  shareToken: string;
  shareUrl: string;
}

/**
 * Response type for getting snapshots
 */
interface GetSnapshotResp {
  id: number;
  shareToken: string;
  title: string;
  description: string | null;
  threadId: number | null;
  expiresAt: string | null;
  viewCount: number;
  createdAt: string;
  updatedAt: string | null;
  shareUrl: string;
}

/**
 * The existing snapshot for a thread, if it has one.
 *
 * Options rather than a hook: its only caller shares a thread, and sharing is
 * rare enough that the request belongs at the moment someone asks for a link
 * rather than on mount. Mounted per row, a hook here put one request on the
 * wire for every visible thread, for an action most of them never take.
 */
export const threadSnapshotQueryOptions = (threadId: number) =>
  queryOptions({
    queryKey: ["thread-snapshot", threadId],
    queryFn: async ({ signal }) => {
      const response = await api.get<GetSnapshotResp[]>(
        `/api/a/shared-snapshots?threadId=${threadId}`,
        { signal },
      );
      // Return the first snapshot for this thread (filtered server-side)
      return response.data[0] ?? null;
    },
    staleTime: 30000, // Cache for 30 seconds
  });

/**
 * Hook to create a shareable snapshot of a thread.
 */
export const useCreateThreadSnapshot = ({ threadId }: { threadId: number }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await api.post<CreateSnapshotResp>(`/api/a/threads/${threadId}/share`, {});
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the snapshot query so it refetches
      queryClient.invalidateQueries({ queryKey: ["thread-snapshot", threadId] });
    },
    onError: (error: Error) => {
      console.error("Failed to create thread snapshot:", error);
      Sentry.captureException(error, {
        tags: {
          component: "useCreateThreadSnapshot",
          operation: "createSnapshot",
        },
        extra: {
          threadId,
        },
      });
    },
  });
};

/**
 * Hook to expose thread execution stopping.
 */
export const useStopThreadExecution = ({ threadId }: { threadId: number }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // optimistically update the thread to the "stopped" state
      // don't worry about revalidation, the thread should push a mutated event in the stop handler
      updateThreadOptimistically(queryClient, threadId, {
        activityState: "stopPending",
      });

      const response = await api.post<void>(`/api/a/threads/${threadId}/stop`, {});

      return response.data;
    },
    onSuccess: () => {
      // revalidate the thread
      queryClient.invalidateQueries({
        queryKey: threadQueries.detail(threadId).queryKey,
      });
    },
  });
};

export const useSessionThread = () => {
  const queryClient = useQueryClient();
  const {
    data: thread,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["threads", "session"] as const,
    queryFn: async ({ signal }) => {
      const response = await api.get<GetThreadResp>(`/api/a/threads/session`, {
        signal,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Error handling
  useEffect(() => {
    if (error) {
      console.error("Failed to fetch session thread:", error);
      Sentry.captureException(error, {
        tags: {
          component: "useSessionThread",
          operation: "fetchSessionThread",
        },
      });
      // TODO: Add user notification toast here
    }
  }, [error]);

  const mutate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["threads", "session"] });
  }, [queryClient]);

  return {
    isLoading,
    error,
    thread,
    mutate,
  };
};

export const createNewTopicThread = async ({
  title,
  artifactIds,
  action,
  config,
}: CreateNewTopicThreadReq): Promise<CreateNewTopicThreadResp> => {
  const response = await api.post<CreateNewTopicThreadResp>(`/api/a/threads/topics/new`, {
    title,
    artifactIds,
    action,
    config,
  });
  return response.data;
};

const threadStatusQueryParam = "threadStatus";

/**
 * Hook to manage status filter state with URL synchronization.
 * Reads the 'status' from URL search params and provides a function to update it.
 */
export const useThreadStatusFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get the status from search params or default to defaultStatusFilter
  const statusFromUrl = searchParams.get(threadStatusQueryParam) || defaultStatusFilter;

  const setStatusFilter = useCallback(
    (status: string) => {
      // Create a new URLSearchParams instance to avoid mutating the original
      const newParams = new URLSearchParams(searchParams);

      if (status === defaultStatusFilter) {
        // Remove the status parameter if it's the default
        newParams.delete(threadStatusQueryParam);
      } else {
        newParams.set(threadStatusQueryParam, status);
      }

      // Update the search params
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return [statusFromUrl, setStatusFilter] as const;
};

export type { ThreadFilterMode };

const filterQueryParam = "filter";

/**
 * Whether any active thread is currently running, so busy indicators (the
 * desktop chat toggle, the mobile Chats tab) can show a spinner. Filters
 * server-side to running threads within the active set, so a running thread
 * can't slip past a client-side page limit — existence is all we need, hence
 * `limit: 1`. This is its own query (the list pane keys on the user's chosen
 * filter mode), backed by `threadsAccountIdActivityStateUpdatedAtIdx`;
 * `refetchInterval` + socket invalidation keep it live, and React Query dedupes
 * it across consumers. Waiting-for-user threads don't count as running.
 */
export const useHasRunningThread = (): boolean => {
  const includeExternalChannels = useGateValue("visible_external_threads");
  const { threadPages } = useThreads({
    ...getThreadFilterParams("active", includeExternalChannels),
    activityState: ["running"],
    limit: 1,
  });
  return threadListRows(threadPages).length > 0;
};

/**
 * Unified hook for the home screen thread filter.
 * Replaces the old useShowArchived + useThreadChannelFilter with a single
 * URL-param-backed enum. Default is "active" (param omitted from URL).
 */
const validFilterModes = new Set<string>(["routines", "all", "archived", "external"]);

export const useThreadFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const includeExternalChannels = useGateValue("visible_external_threads");

  const raw = searchParams.get(filterQueryParam);
  const filterMode: ThreadFilterMode =
    raw && validFilterModes.has(raw) ? (raw as ThreadFilterMode) : "active";

  // Strip unrecognized filter values from the URL to keep it clean
  useEffect(() => {
    if (raw && !validFilterModes.has(raw)) {
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete(filterQueryParam);
      setSearchParams(cleaned, { replace: true });
    }
  }, [raw, searchParams, setSearchParams]);

  const setFilterMode = useCallback(
    (mode: ThreadFilterMode) => {
      const newParams = new URLSearchParams(searchParams);
      if (mode === "active") {
        newParams.delete(filterQueryParam);
      } else {
        newParams.set(filterQueryParam, mode);
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const filterParams = getThreadFilterParams(filterMode, includeExternalChannels);

  return { filterMode, setFilterMode, filterParams } as const;
};

/**
 * Hook to expose thread unarchiving using React Query.
 * Restores a completed (archived) thread back to in-progress status.
 */
/**
 * Unarchive a thread, defaulting to the one this hook was mounted against.
 *
 * The target is accepted as a mutation variable because a caller can outlive
 * the mount's identity: the thread list mounts this against whichever row has
 * focus, and an Undo affordance stays on screen while focus moves on. Passing
 * the id the caller captured is the only way to guarantee the thread that was
 * archived is the thread that comes back.
 */
export const useUnarchiveThread = ({ threadId }: { threadId: number }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (targetId: number = threadId) => {
      const response = await api.post(`/api/a/threads/${targetId}/unarchive`, {});
      return response.data;
    },
    onMutate: async (targetId = threadId) => {
      // Optimistically update the thread status back to to-review or in-progress
      updateThreadOptimistically(queryClient, targetId, {
        status: "to-review",
      });
    },
    onSuccess: (_data, targetId = threadId) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: threadQueries.detail(targetId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: threadQueries.allLists() });
      // Unarchiving can make the thread eligible for the badge tallies, so
      // refresh the badge to keep it in sync (mirrors archive).
      queryClient.invalidateQueries({ queryKey: badgeQueries.all() });
    },
    onError: (_error, targetId = threadId) => {
      // Revert optimistic update on error. Lists carry the optimistic status
      // too, so detail alone would leave every row reading unarchived.
      queryClient.invalidateQueries({
        queryKey: threadQueries.detail(targetId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: threadQueries.allLists() });
    },
  });
};

/**
 * Hook for inline-editing a thread's title from the thread detail view.
 * Optimistically updates the cache so the edit reflects immediately, rolls
 * back on error. Empty string clears the title — the server-side auto-
 * title-gen pass will refill it on the next agent turn.
 */
export const useUpdateThreadTitle = ({ threadId }: { threadId: number }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (title: string) => {
      const response = await api.post<{ success: boolean }, UpdateThreadReq>(
        `/api/a/threads/${threadId}`,
        { title },
      );
      return response.data;
    },
    onMutate: async (title) => {
      // Cancel any in-flight refetches so a resolving background request
      // doesn't overwrite our optimistic title with stale server data.
      await queryClient.cancelQueries({ queryKey: threadQueries.detail(threadId).queryKey });
      await queryClient.cancelQueries({ queryKey: threadQueries.allLists() });
      updateThreadOptimistically(queryClient, threadId, { title });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: threadQueries.detail(threadId).queryKey });
      queryClient.invalidateQueries({ queryKey: threadQueries.allLists() });
    },
    onError: () => {
      // Roll back both caches — `updateThreadOptimistically` patched both
      // the detail query and every list query, so invalidating only detail
      // leaves home cards showing the failed optimistic title until the
      // next natural refetch.
      queryClient.invalidateQueries({ queryKey: threadQueries.detail(threadId).queryKey });
      queryClient.invalidateQueries({ queryKey: threadQueries.allLists() });
    },
  });
};
